import type { GeneratedNodeAttribute, GeneratedStatus, InstanceContact, ResponsibleRef } from "@keydials/api-client";

// ============================================================
// Status gates (KEY-248), client side: a status can require named node-attribute fields to be filled in
// before it may be applied. The server is the authority (InstanceService.FirstUnfilledRequiredAttributeAsync
// refuses the change with MissingRequiredAttribute); this module lets the workspace answer the same
// question up front so the UI can walk the user through the fields instead of bouncing off a 400.
//
// Parity rules mirrored from the server:
// - "Filled" means a value exists and isn't whitespace-only (a cleared value is stored as no row at all).
// - Gate keys the version doesn't declare, or whose attribute is scoped away from this node, are ignored —
//   such a field can't be filled in here, so demanding it would leave the status permanently unsettable.
//   Callers get this for free by passing declarations already filtered to the node (the same
//   attributeScopeApplies / itemScopeApplies lists the panel renders from).
// ============================================================

// ============================================================
// Review-date rules (KEY-412), client side. Same contract as the attribute gate above: the server is the
// authority (InstanceService.ReviewDateRefusal returns ReviewDateNotAllowed / ReviewDateRequired) and this
// mirrors it so the workspace can offer — or insist on — a date up front instead of bouncing off a 400.
//
// Parity rules mirrored from the server:
// - A missing/unrecognised policy is "Optional": what every status meant before the field existed.
// - The per-item switch CLAMPS the policy to NotOffered. That is what keeps a Required status settable on
//   an item that does no expiries — a requirement the node can never satisfy is ignored, not enforced,
//   exactly as a gate attribute scoped away from a node is ignored.
// ============================================================

/** The three review-date policies a status can carry. */
export type ReviewDatePolicy = "Optional" | "NotOffered" | "Required";

/**
 * The policy actually in force for a status on a node: the status's own, narrowed by the node's expiry
 * switch. Pass `nodeAllowsReviewDate: true` for an action point, which has no such switch.
 */
export function effectiveReviewDatePolicy(
  status: Pick<GeneratedStatus, "reviewDatePolicy"> | undefined,
  nodeAllowsReviewDate: boolean,
): ReviewDatePolicy {
  if (!nodeAllowsReviewDate) {
    return "NotOffered";
  }
  const policy = status?.reviewDatePolicy;
  return policy === "NotOffered" || policy === "Required" ? policy : "Optional";
}

/** Whether a review date may be scheduled on a node currently sitting at `status`. */
export const reviewDateOffered = (
  status: Pick<GeneratedStatus, "reviewDatePolicy"> | undefined,
  nodeAllowsReviewDate: boolean,
): boolean => effectiveReviewDatePolicy(status, nodeAllowsReviewDate) !== "NotOffered";

/**
 * Whether picking `target` needs a review date supplied with it — i.e. the pick cannot be committed on its
 * own. False once the node already carries a date: the server counts that date as satisfying the status and
 * keeps it through the change (`KeepsStoredReviewDate` in InstanceService), so the pick goes through bare.
 */
export function reviewDateRequiredFor(
  target: Pick<GeneratedStatus, "reviewDatePolicy"> | undefined,
  nodeAllowsReviewDate: boolean,
  currentReviewDate: string | null | undefined,
): boolean {
  return effectiveReviewDatePolicy(target, nodeAllowsReviewDate) === "Required" && !currentReviewDate;
}

/**
 * Whether moving a node TO `target` needs a comment saying why (KEY-919). The server is the authority — it
 * refuses a blank `note` with a 400 — and exempts re-setting the status a node already holds, so callers
 * only ask this for a genuine change. Missing/null `requiresComment` means no.
 */
export const commentRequiredFor = (
  target: Pick<GeneratedStatus, "requiresComment"> | undefined,
): boolean => target?.requiresComment === true;

/** Who is asking, for the audience rule (KEY-918). */
export interface AudienceViewer {
  userId: string | null;
  email: string | null;
  /** A guest (external) session — `InstanceDetail.viewerIsGuest`. */
  isGuest: boolean;
}

/**
 * Whether the viewer is someone `status` is for (KEY-918) — the second half of "may I set this?", after the
 * roles. A dial admin always is. "Responsible" asks who the Responsible person field names at the node
 * (`responsible`, already resolved with inheritance): a member by user id, a contact through the guest account
 * invited at their email. Nobody named → nobody but an admin. The server is the authority; this only greys
 * the option out so nobody picks a status the write would refuse.
 */
export function audienceAllows(
  status: Pick<GeneratedStatus, "audience">,
  isAdmin: boolean,
  viewer: AudienceViewer,
  responsible: ResponsibleRef | null,
  contacts: readonly Pick<InstanceContact, "id" | "email">[],
): boolean {
  const audience = status.audience ?? "Everyone";
  if (isAdmin || audience === "Everyone") return true;
  if (audience === "Members") return !viewer.isGuest;
  if (audience === "Guests") return viewer.isGuest;
  if (!responsible) return false;
  if (responsible.kind === "Member") return !!viewer.userId && responsible.id === viewer.userId.toLowerCase();
  const email = contacts.find((c) => c.id.toLowerCase() === responsible.id)?.email;
  return viewer.isGuest && !!email && !!viewer.email && email.trim().toLowerCase() === viewer.email.trim().toLowerCase();
}

/** Whether a stored attribute value counts as filled for a gate. */
export const gateValueFilled = (value: string): boolean => value.trim() !== "";

/**
 * Every attribute key any status in `statuses` gates on. Fields named here are the ones the panel keeps
 * hidden while empty — they only surface when a pick demands them (or once they carry a value).
 */
export function gateKeys(statuses: readonly GeneratedStatus[]): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const status of statuses) {
    for (const key of status.requiredAttributeKeys ?? []) {
      keys.add(key);
    }
  }
  return keys;
}

/**
 * The attributes `target` requires that this node hasn't filled in yet, in declaration order (the order the
 * server reports them in). Empty when the status has no gate or every gated field carries a value — i.e.
 * when the pick can be committed immediately.
 */
export function missingGateAttributes(
  target: GeneratedStatus,
  nodeDecls: readonly GeneratedNodeAttribute[],
  valueOf: (attrKey: string) => string,
): GeneratedNodeAttribute[] {
  const required = new Set(target.requiredAttributeKeys ?? []);
  if (required.size === 0) {
    return [];
  }
  return nodeDecls
    .filter((d) => required.has(d.key) && !gateValueFilled(valueOf(d.key)))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}
